import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { formatNaira } from '@/lib/utils';
import { MagnifyingGlass, Plus, Bell, UserCircle, House, Briefcase, ChatCircle, Wrench, Star, ShieldCheck, Drop, Lightning, Snowflake, Hammer, PaintBrush, SquaresFour, Sun, Car, Broom, Wall, ArrowRight, MapPin, Sparkle } from '@phosphor-icons/react';

const ICON_MAP = { plumbing: Drop, electrical: Lightning, ac_cooling: Snowflake, carpentry: Hammer, painting: PaintBrush, tiling: SquaresFour, welding: Wrench, solar_power: Sun, auto_services: Car, cleaning: Broom, interior: House, masonry: Wall };

export default function HomePage() {
  const { user, api } = useAuth();
  const notifCtx = useNotifications();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ open_jobs: 0, total_artisans: 0, completed_jobs: 0 });
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, statsRes] = await Promise.all([
          api.get('/categories'),
          api.get('/admin/stats').catch(() => ({ data: {} })),
        ]);
        setCategories(catRes.data.categories || []);
        if (statsRes.data) setStats(statsRes.data);
      } catch {}
    };
    load();
  }, [api]);

  const filtered = search ? categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.subs?.some(s => s.toLowerCase().includes(search.toLowerCase()))) : categories;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24" data-testid="home-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-[480px] mx-auto px-4 pt-3 pb-3">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-heading text-2xl font-black text-[#1A4D2E]">Naija<span className="text-[#E86A33]">Fix</span> <span className="text-[10px] font-body font-semibold text-[#8C8178] tracking-wider">Artisans on the go</span></h1>
            <div className="flex items-center gap-2">
              <Link to={user ? '/notifications' : '/login'} className="w-10 h-10 rounded-full bg-[#E8DDD2] flex items-center justify-center relative" data-testid="notifications-button">
                <Bell size={20} weight="duotone" className="text-[#5C5C5C]" />
                {notifCtx?.unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#E86A33] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">{notifCtx.unreadCount}</span>}
              </Link>
              <Link to={user ? '/profile' : '/login'} className="w-10 h-10 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white font-bold text-sm" data-testid="profile-avatar">
                {user ? user.name?.charAt(0)?.toUpperCase() || 'U' : <UserCircle size={20} />}
              </Link>
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={18} />
            <input data-testid="search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search for a service or artisan..." className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-xl pl-10 pr-4 py-3 text-sm text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none font-body placeholder:text-[#5C5C5C]/60" />
          </div>
        </div>
      </header>

      <div className="max-w-[480px] mx-auto px-4">
        {/* Stat Pills */}
        <div className="flex gap-2 overflow-x-auto py-3 -mx-4 px-4 scrollbar-hide">
          <div className="flex-shrink-0 bg-[#1A4D2E]/5 border border-[#1A4D2E]/15 rounded-full px-3 py-1.5 text-xs font-bold text-[#1A4D2E]" data-testid="stat-artisans">{stats.total_artisans || 0}+ Artisans</div>
          <div className="flex-shrink-0 bg-[#E86A33]/5 border border-[#E86A33]/15 rounded-full px-3 py-1.5 text-xs font-bold text-[#E86A33]" data-testid="stat-open-jobs">{stats.open_jobs || 0} Open Jobs</div>
          <div className="flex-shrink-0 bg-[#2D6A4F]/5 border border-[#2D6A4F]/15 rounded-full px-3 py-1.5 text-xs font-bold text-[#2D6A4F]" data-testid="stat-completed">{stats.completed_jobs || 0} Completed</div>
        </div>

        {/* Quick Quote CTA */}
        <Link to="/quick-quote" className="block mx-auto mb-4 bg-gradient-to-r from-[#1A4D2E]/5 to-[#E86A33]/5 border border-[#1A4D2E]/15 rounded-2xl p-4 hover:-translate-y-0.5 transition-all" data-testid="quick-quote-cta">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E86A33]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkle size={20} weight="duotone" className="text-[#E86A33]" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-[#1C1C1C] font-body">Quick Quote</h4>
              <p className="text-xs text-[#5C5C5C] font-body">Get AI-powered price estimate — no signup needed</p>
            </div>
            <ArrowRight size={16} className="text-[#E86A33]" />
          </div>
        </Link>

        {/* Hero CTA */}
        <div className="relative bg-gradient-to-br from-[#1A4D2E] to-[#133922] rounded-2xl p-6 overflow-hidden mb-4 cursor-pointer" onClick={() => user ? navigate('/post-job') : navigate('/login')} data-testid="hero-cta">
          <div className="absolute top-[-20px] right-[-20px] w-[130px] h-[130px] bg-white/5 rounded-full" />
          <div className="absolute bottom-[-30px] right-10 w-[90px] h-[90px] bg-white/5 rounded-full" />
          <span className="inline-block bg-white/15 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Verified Artisans Near You</span>
          <h2 className="font-heading text-xl font-bold text-white leading-tight mb-4">Get any home job<br />done — safely & fast</h2>
          <button className="bg-white text-[#1A4D2E] font-bold text-sm px-5 py-2.5 rounded-lg flex items-center gap-2 relative z-10 hover:-translate-y-0.5 transition-all duration-200" data-testid="post-job-hero-button">
            <Plus size={16} weight="bold" /> Post a Job
          </button>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-3 flex items-center gap-2">
            <Wrench size={20} weight="duotone" className="text-[#E86A33]" />
            <div><div className="font-heading font-bold text-sm text-[#1C1C1C]">1,240+</div><div className="text-[10px] text-[#5C5C5C]">Artisans</div></div>
          </div>
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-3 flex items-center gap-2">
            <Star size={20} weight="duotone" className="text-[#F4A261]" />
            <div><div className="font-heading font-bold text-sm text-[#1C1C1C]">4.8</div><div className="text-[10px] text-[#5C5C5C]">Avg Rating</div></div>
          </div>
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-3 flex items-center gap-2">
            <ShieldCheck size={20} weight="duotone" className="text-[#2D6A4F]" />
            <div><div className="font-heading font-bold text-sm text-[#1C1C1C]">100%</div><div className="text-[10px] text-[#5C5C5C]">Verified</div></div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-[#1C1C1C]">Browse Services</h3>
          <span className="text-xs font-bold text-[#E86A33]">{categories.length} categories</span>
        </div>
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {filtered.map(cat => {
            const Icon = ICON_MAP[cat.id] || Wrench;
            return (
              <button key={cat.id} data-testid={`category-${cat.id}`} onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                className={`bg-white border rounded-xl p-3 flex flex-col items-center gap-2 text-center transition-all duration-200 hover:-translate-y-0.5 ${activeCat === cat.id ? 'border-[#E86A33] bg-[#E86A33]/5 shadow-sm' : 'border-[#D4C5B3]'}`}>
                <Icon size={28} weight="duotone" className={activeCat === cat.id ? 'text-[#E86A33]' : 'text-[#1A4D2E]'} />
                <span className="text-xs font-semibold text-[#1C1C1C] font-body">{cat.name}</span>
                <span className="text-[10px] text-[#5C5C5C]">{cat.subs?.length || 0} services</span>
              </button>
            );
          })}
        </div>

        {/* Sub-services panel */}
        {activeCat && (() => {
          const cat = categories.find(c => c.id === activeCat);
          if (!cat) return null;
          const Icon = ICON_MAP[cat.id] || Wrench;
          return (
            <div className="bg-white border-2 border-[#E86A33] rounded-xl overflow-hidden mb-6 animate-in slide-in-from-top-2" data-testid="sub-services-panel">
              <div className="bg-[#E86A33] px-4 py-3 flex items-center justify-between">
                <span className="text-white font-bold flex items-center gap-2 font-body"><Icon size={18} /> {cat.name}</span>
                <button onClick={() => setActiveCat(null)} className="text-white/80 hover:text-white text-lg">&times;</button>
              </div>
              <div className="p-2">
                {cat.subs?.map(sub => (
                  <Link key={sub} to={user ? `/post-job?category=${cat.id}&sub=${encodeURIComponent(sub)}` : '/login'} className="flex items-center justify-between p-3 hover:bg-[#E8DDD2] rounded-lg transition-colors" data-testid={`sub-service-${sub.replace(/\s/g, '-').toLowerCase()}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#E86A33] rounded-full" />
                      <span className="text-sm font-medium text-[#1C1C1C] font-body">{sub}</span>
                    </div>
                    <ArrowRight size={14} className="text-[#5C5C5C]" />
                  </Link>
                ))}
              </div>
              <Link to={user ? `/post-job?category=${cat.id}` : '/login'} className="block mx-3 mb-3 bg-[#E86A33] text-white text-center font-bold py-3 rounded-xl hover:bg-[#D35A29] transition-all font-body" data-testid="post-job-category-button">
                <Plus size={16} weight="bold" className="inline mr-1" /> Post a Job in {cat.name}
              </Link>
            </div>
          );
        })()}

        {/* How It Works */}
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-3">How it works</p>
          <div className="grid grid-cols-2 gap-2.5">
            {[
              { icon: '1', title: 'Post a Job', desc: 'Describe the work, get AI cost estimate' },
              { icon: '2', title: 'Receive Bids', desc: 'Verified artisans submit itemised bids' },
              { icon: '3', title: 'Escrow Payment', desc: 'Funds held safely until work is approved' },
              { icon: '4', title: 'Rate & Review', desc: 'Trust scores protect both sides' },
            ].map(item => (
              <div key={item.title} className="bg-white border border-[#D4C5B3] rounded-xl p-4">
                <div className="w-8 h-8 rounded-full bg-[#1A4D2E] text-white flex items-center justify-center text-sm font-bold mb-3">{item.icon}</div>
                <div className="text-sm font-bold text-[#1C1C1C] mb-1 font-body">{item.title}</div>
                <div className="text-xs text-[#5C5C5C] leading-relaxed font-body">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-t border-black/5" data-testid="bottom-nav">
        <div className="max-w-[480px] mx-auto flex items-center py-2 pb-5">
          <Link to="/" className="flex-1 flex flex-col items-center gap-1 text-[#E86A33]" data-testid="nav-home">
            <House size={22} weight="duotone" /><span className="text-[10px] font-semibold">Home</span>
          </Link>
          <Link to={user ? '/my-jobs' : '/login'} className="flex-1 flex flex-col items-center gap-1 text-[#5C5C5C]" data-testid="nav-jobs">
            <Briefcase size={22} weight="duotone" /><span className="text-[10px] font-semibold">My Jobs</span>
          </Link>
          <div className="flex-1 flex flex-col items-center">
            <Link to={user ? '/post-job' : '/login'} className="w-14 h-14 -mt-5 bg-[#E86A33] rounded-full flex items-center justify-center text-white shadow-lg shadow-[#E86A33]/30 border-4 border-white hover:scale-105 transition-transform" data-testid="nav-post-job">
              <Plus size={26} weight="bold" />
            </Link>
            <span className="text-[10px] font-semibold text-[#E86A33] mt-1">Post Job</span>
          </div>
          <Link to={user ? '/chat' : '/login'} className="flex-1 flex flex-col items-center gap-1 text-[#5C5C5C]" data-testid="nav-chat">
            <ChatCircle size={22} weight="duotone" /><span className="text-[10px] font-semibold">Messages</span>
          </Link>
          <Link to={user ? '/profile' : '/login'} className="flex-1 flex flex-col items-center gap-1 text-[#5C5C5C]" data-testid="nav-profile">
            <UserCircle size={22} weight="duotone" /><span className="text-[10px] font-semibold">Profile</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
